import { useEffect, useRef } from "react";

/**
 * Custom hook for recursive timeout with proper cleanup.
 * Useful for auto-refresh functionality that waits for the previous
 * operation to complete before scheduling the next one.
 *
 * @param {Function} callback - Function to call on each interval
 * @param {number|null} delay - Delay in ms, or null to pause
 * @param {boolean} enabled - Whether auto-refresh is enabled
 */
export function useRecursiveTimeout(callback, delay, enabled) {
  const savedCallback = useRef(callback);

  // Update the callback ref when it changes
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    if (!enabled || delay === null) return;

    let timeoutId;

    const tick = async () => {
      try {
        const result = savedCallback.current();
        if (result instanceof Promise) {
          await result;
        }
      } catch (error) {
        console.error("Auto-refresh error:", error);
      }
      // Schedule next tick after the current one completes
      if (enabled) {
        timeoutId = setTimeout(tick, delay);
      }
    };

    // Start the first timeout
    timeoutId = setTimeout(tick, delay);

    // Cleanup on unmount or when dependencies change
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [delay, enabled]);
}
